import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

/** Material-UI */
import { useTheme } from '@mui/material';
import Container from '@mui/material/Container';

/** Components */
import CBreadcrumbs from 'components/CBreadcrumbs';
import CategoryPageSeo from 'components/Seo/CategoryPageSeo';
import CategoryDescription from 'components/Category/CategoryDescription';
import CategoryChildrenList from 'components/Category/CategoryChildrenList';
import CategoryBrandTabContent from 'components/Category/CategoryBrandTabContent';
import CategoryProductTabContent from 'components/Category/CategoryProductTabContent';

/** Utils / Consts */
import { filterUrlQueryParams } from 'constants/category-filters';
import { DEFAULT_BRANDS_LIMIT, DEFAULT_PRODUCTS_LIMIT, DEFAULT_TAB_LISTING_PAGES } from 'constants/listing-defaults';

/** Hooks */
import useAuth from 'hooks/useAuth';
import useResponsive from 'hooks/useResponsive';

/* Layout */
import FullWidthLayout from 'layouts/FullWidthLayout';

/** Sections */
import CustomTabs from 'components/CustomTabs';
import FiltersProvider from 'provider/FiltersProvider';
import VirgoFilters, { VIRGO_FILTERS } from 'components/VirgoFilters';
import { usePreserveScroll } from 'hooks/usePreserveScroll';
import Head from 'next/head';
import SignUpCTA from '../../sections/ctas/SignUpCTA';

const DEFAULT_LIMITS = {
  brands: DEFAULT_BRANDS_LIMIT,
  products: DEFAULT_PRODUCTS_LIMIT,
};

const legacyCategoryLayoutDefaultPropCategoryData = {
  slug: '',
  categoryData: {
    name: '',
    slug: '',
    description: '',
    shortDescription: '',
    seoTitle: '',
    seoDescription: '',
    parent: {},
    children: [],
  },
};

const legacyCategoryLayoutDefaultPropInitialProductsData = {
  totalProducts: 0,
  products: [],
};

const legacyCategoryLayoutDefaultPropInitialBrandsData = {
  totalBrands: 0,
  brands: [],
};

const legacyCategoryLayoutDefaultPropInitialQueryParams = {};
const legacyCategoryLayoutDefaultPropInitialBreadcrumbs = [];

const LegacyCategoryLayout = ({
  slug,
  initialTab = DEFAULT_TAB_LISTING_PAGES,
  categoryData = legacyCategoryLayoutDefaultPropCategoryData,
  initialBrandsData = legacyCategoryLayoutDefaultPropInitialBrandsData,
  initialQueryParams = legacyCategoryLayoutDefaultPropInitialQueryParams,
  initialProductsData = legacyCategoryLayoutDefaultPropInitialProductsData,
  initialBreadcrumbs = legacyCategoryLayoutDefaultPropInitialBreadcrumbs,
  shouldRenderFilter = true,
}) => {
  const theme = useTheme();
  const router = useRouter();
  const { query, pathname, asPath } = router;
  const { isAuthenticated } = useAuth();

  const isTablet = useResponsive('down', 'md');
  const isDesktop = useResponsive('up', 'lg');
  // When we are using an Ipad Air horizontally, the filters are not visible to the user because the variables isTablet and isDesktop have a false value as we do not have any media query covering this measurement, so by creating this variable checkIfIsDesktop and using this mechanism we can guarantee that filters are visible
  const checkIfIsDesktop = isDesktop || !isTablet;

  const baseUrl = `https://www.creoate.com`;

  const { itemId, updateItemId } = usePreserveScroll(filterUrlQueryParams.ITEM_ID);

  const currentTab = query?.tab || initialTab;

  const [limit, setLimit] = useState({
    brands: (currentTab === 'brands' && Number(query.limit)) || DEFAULT_BRANDS_LIMIT,
    products: (currentTab === 'products' && Number(query.limit)) || DEFAULT_PRODUCTS_LIMIT,
  });

  // Check if filter parameters exist in the URL
  const filterParamsExist = Object.keys(router.query).some(
    (key) => key !== 'slug' // Assuming 'slug' is always part of the URL
  );

  /** Handler functions */
  const handleLimitChange = () => {
    setLimit((prevState) => {
      router.push(
        {
          ...router,
          query: {
            ...router.query,
            limit: Number(prevState[currentTab]) + DEFAULT_LIMITS[currentTab],
          },
        },
        undefined,
        { shallow: true }
      );

      return {
        ...prevState,
        [currentTab]: Number(prevState[currentTab]) + DEFAULT_LIMITS[currentTab],
      };
    });
  };

  const handleItemIdChange = (newItemId, resetItemId = false) => {
    if (!newItemId && !resetItemId) {
      return;
    }

    updateItemId(newItemId);
  };

  const handleTabChange = () => {
    handleItemIdChange('', true);
  };

  const queryParametersHandlers = {
    handleLimitChange,
    handleItemIdChange,
    itemId,
  };

  // Reset limit when we change slug
  useEffect(() => {
    setLimit({
      brands: DEFAULT_BRANDS_LIMIT,
      products: DEFAULT_PRODUCTS_LIMIT,
    });

    const newQuery = { ...router.query };
    delete newQuery.limit;
    router.replace(
      {
        pathname: router.pathname,
        query: newQuery,
      },
      undefined,
      { shallow: true }
    );
  }, [slug]);

  const filtersToBeApplied = [
    { id: VIRGO_FILTERS.COUNTRIES },
    { id: VIRGO_FILTERS.MIN_ORDER },
    { id: VIRGO_FILTERS.LEAD_TIME_MAX_DAYS },
    { id: VIRGO_FILTERS.BRAND_VALUE },
    { id: VIRGO_FILTERS.FLAIRS },
    { id: VIRGO_FILTERS.ON_OFFER },
    // TODO: The below code is being commented until we refine and implement a proper solution for Brands You Might Like business logic { id: VIRGO_FILTERS.BRANDS_YOU_MIGHT_LIKE, authProtected: true },
    { id: VIRGO_FILTERS.SORT_BY, authProtected: true },
  ];

  const defaultTabIndex = ['products', 'brands'].findIndex((tab) => tab === currentTab);

  return (
    <>
      <CategoryPageSeo
        category={categoryData}
        url={baseUrl + asPath}
        canonical={baseUrl + pathname}
        seoTitle={categoryData.seoTitle}
        seoDescription={categoryData.seoDescription}
        products={initialProductsData?.products.slice(0, 5)}
      />
      <FiltersProvider initialQueryParams={initialQueryParams}>
        {isTablet && shouldRenderFilter && <VirgoFilters filters={filtersToBeApplied} defaultTab={currentTab} />}
        {filterParamsExist && (
          <Head>
            <meta name="robots" content="noindex" />
          </Head>
        )}
        <Container
          maxWidth={false}
          sx={{
            py: 1.25,
            [theme.breakpoints.down('md')]: {
              px: theme.spacing(1),
              pt: 1,
            },
          }}
        >
          <CBreadcrumbs
            sx={{ mb: -1, ml: 1 }}
            onBreadcrumbClick={(breadcrumb) => {
              if (breadcrumb.slug === '/') {
                router.push(`/`);
              } else if (router.query.slug !== breadcrumb.slug) {
                router.push(`/product-category/${breadcrumb.slug}`);
              }
            }}
            initialBreadcrumbs={initialBreadcrumbs}
          />
          <CategoryChildrenList
            category={categoryData}
            sx={{
              mt: 1,
              [theme.breakpoints.up('md')]: {
                mb: 2.5,
              },
            }}
          />
          {checkIfIsDesktop && (
            <VirgoFilters
              filters={filtersToBeApplied}
              defaultTab={currentTab}
              sx={
                !isTablet && {
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginTop: 1,
                }
              }
            />
          )}

          <CustomTabs
            tabs={[
              {
                tabName: 'brands',
                tabLimit: limit.brands,
                tabContent: (
                  <CategoryBrandTabContent
                    slug={slug}
                    {...queryParametersHandlers}
                    initialBrandsData={initialBrandsData}
                  />
                ),
              },
              {
                tabName: 'products',
                tabLimit: limit.products,
                tabContent: (
                  <CategoryProductTabContent
                    slug={slug}
                    {...queryParametersHandlers}
                    initialProductsData={initialProductsData}
                  />
                ),
              },
            ]}
            config={{
              label: 'product-category',
              defaultTabIndex: {
                authenticated: defaultTabIndex,
                nonAuthenticated: defaultTabIndex,
              },
            }}
            onTabChange={handleTabChange}
            sx={isTablet ? { mb: 2 } : { my: 2 }}
          />
        </Container>
      </FiltersProvider>
      {!isAuthenticated && <SignUpCTA />}
      <CategoryDescription category={categoryData} sx={{ pl: 3, my: 3.75 }} />
    </>
  );
};

LegacyCategoryLayout.getLayout = FullWidthLayout;

LegacyCategoryLayout.propTypes = {
  slug: PropTypes.string.isRequired,
  categoryData: PropTypes.shape({
    name: PropTypes.string,
    slug: PropTypes.string,
    description: PropTypes.string,
    shortDescription: PropTypes.string,
    seoTitle: PropTypes.string,
    seoDescription: PropTypes.string,
    parent: PropTypes.shape({}),
    children: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  initialProductsData: PropTypes.shape({
    totalProducts: PropTypes.number,
    products: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  initialBrandsData: PropTypes.shape({
    totalBrands: PropTypes.number,
    brands: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  initialQueryParams: PropTypes.shape({}),
  initialTab: PropTypes.string,
  initialBreadcrumbs: PropTypes.arrayOf(PropTypes.shape({})),
  shouldRenderFilter: PropTypes.bool,
};

export default LegacyCategoryLayout;
